import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { ChevronDown, ChevronUp, Settings } from "lucide-react";
import axios from "axios";
import { AppRadio } from "../components/form/AppRadio.jsx";
import "./PaymentLinksPage.css";

const shortFopName = (f) => f.legalName.replace(/^ФОП\s+/i, "");

/** Порядок ФОП і ФОП за замовчуванням (лише admin / finance) */
function FopSettingsModal({ show, fops, defaultFopId, onHide, onSaved }) {
    const [order, setOrder] = useState(fops);
    const [defaultId, setDefaultId] = useState(defaultFopId || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!show) return;
        setOrder(fops);
        setDefaultId(defaultFopId || "");
        setError("");
    }, [show, fops, defaultFopId]);

    const move = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= order.length) return;
        const next = [...order];
        [next[index], next[target]] = [next[target], next[index]];
        setOrder(next);
    };

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const { data } = await axios.put("/api/payment-links/settings", {
                fopOrder: order.map((f) => f.id),
                defaultFopId: defaultId || null,
            });
            onSaved(data);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title className="h6 mb-0">Налаштування ФОП</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="payment-links-label">Порядок і ФОП за замовчуванням</div>
                {order.map((f, i) => (
                    <div key={f.id} className="payment-links-settings-row">
                        <AppRadio
                            name="default-fop"
                            id={`default-fop-${f.id}`}
                            value={f.id}
                            checked={defaultId === f.id}
                            onChange={() => setDefaultId(f.id)}
                            label={shortFopName(f)}
                            className="flex-grow-1 mb-0"
                        />
                        <Button size="sm" variant="outline-secondary" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Вище">
                            <ChevronUp size={16} />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={i === order.length - 1}
                            onClick={() => move(i, 1)}
                            aria-label="Нижче"
                        >
                            <ChevronDown size={16} />
                        </Button>
                    </div>
                ))}
                {defaultId && (
                    <Button variant="link" size="sm" className="px-0" onClick={() => setDefaultId("")}>
                        Без ФОП за замовчуванням
                    </Button>
                )}
                {error && (
                    <Alert variant="danger" className="mt-2 mb-0 py-2">
                        {error}
                    </Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Скасувати
                </Button>
                <Button onClick={save} disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : "Зберегти"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        return false;
    }
}

const formatDate = (iso) =>
    new Date(iso).toLocaleString("uk-UA", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function PaymentLinksPage() {
    const [fops, setFops] = useState([]);
    const [fopId, setFopId] = useState("");
    const [defaultFopId, setDefaultFopId] = useState(null);
    const [canEditSettings, setCanEditSettings] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [amount, setAmount] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");
    const [created, setCreated] = useState(null);
    const [copied, setCopied] = useState("");
    const [history, setHistory] = useState([]);
    const amountRef = useRef(null);

    const loadHistory = useCallback(async () => {
        const { data } = await axios.get("/api/payment-links");
        setHistory(data.items || []);
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [{ data }] = await Promise.all([axios.get("/api/payment-links/fops"), loadHistory()]);
                const items = data.items || [];
                setFops(items);
                setDefaultFopId(data.defaultFopId || null);
                setCanEditSettings(!!data.canEditSettings);
                setFopId(data.defaultFopId || items[0]?.id || "");
            } catch (e) {
                setError(e.response?.data?.error || e.message);
            } finally {
                setLoading(false);
            }
        })();
    }, [loadHistory]);

    const selectFop = (id) => {
        setFopId(id);
        amountRef.current?.focus();
    };

    const flashCopied = (key) => {
        setCopied(key);
        setTimeout(() => setCopied((c) => (c === key ? "" : c)), 1500);
    };

    const handleCopy = async (text, key) => {
        if (await copyText(text)) flashCopied(key);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setCreating(true);
        try {
            const { data } = await axios.post("/api/payment-links", { fopUserId: fopId, amount });
            setCreated(data);
            setAmount("");
            if (await copyText(data.url)) flashCopied("created");
            loadHistory().catch(() => {});
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setCreating(false);
        }
    };

    const selectedFop = fops.find((f) => f.id === fopId);

    return (
        <div className="payment-links-page container py-4">
            <div className="d-flex align-items-center justify-content-between mb-3">
                <h1 className="h4 mb-0">Посилання на оплату</h1>
                {canEditSettings && fops.length > 0 && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowSettings(true)}
                        aria-label="Налаштування ФОП"
                        title="Порядок ФОП і ФОП за замовчуванням"
                    >
                        <Settings size={18} />
                    </Button>
                )}
            </div>

            <FopSettingsModal
                show={showSettings}
                fops={fops}
                defaultFopId={defaultFopId}
                onHide={() => setShowSettings(false)}
                onSaved={(data) => {
                    setFops(data.items || []);
                    setDefaultFopId(data.defaultFopId || null);
                    if (data.defaultFopId) setFopId(data.defaultFopId);
                    setShowSettings(false);
                }}
            />

            {loading ? (
                <Spinner animation="border" size="sm" />
            ) : fops.length === 0 ? (
                <Alert variant="warning">
                    Немає ФОП із заповненими реквізитами. Заповніть ІПН та IBAN у розділі «Користувачі» → «Реквізити ФОП».
                </Alert>
            ) : (
                <div className="payment-links-layout">
                    <Form className="payment-links-card" onSubmit={handleSubmit}>
                        <div className="payment-links-label">ФОП</div>
                        <div className="payment-links-fops">
                            {fops.map((f) => (
                                <button
                                    type="button"
                                    key={f.id}
                                    className={`payment-links-fop${f.id === fopId ? " active" : ""}`}
                                    onClick={() => selectFop(f.id)}
                                >
                                    {shortFopName(f)}
                                </button>
                            ))}
                        </div>
                        {selectedFop && (
                            <div className="payment-links-requisites">
                                {selectedFop.edrpou} · {selectedFop.iban}
                            </div>
                        )}

                        <Form.Group className="mt-3">
                            <div className="payment-links-label">Сума, ₴</div>
                            <Form.Control
                                ref={amountRef}
                                inputMode="decimal"
                                placeholder="0.00"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
                                className="payment-links-amount"
                                autoFocus
                            />
                        </Form.Group>
                        <div className="payment-links-hint">Призначення: «Оплата за товар»</div>

                        {error && (
                            <Alert variant="danger" className="mt-3 mb-0 py-2">
                                {error}
                            </Alert>
                        )}

                        <Button type="submit" className="w-100 mt-3" size="lg" disabled={creating || !fopId || !amount}>
                            {creating ? <Spinner animation="border" size="sm" /> : "Створити посилання"}
                        </Button>
                    </Form>

                    {created && (
                        <div className="payment-links-card payment-links-result">
                            <div className="payment-links-result-amount">{created.amountText}</div>
                            <div className="payment-links-result-name">{created.legalName}</div>
                            <div className="payment-links-qr" dangerouslySetInnerHTML={{ __html: created.qrSvg }} />
                            <div className="payment-links-url">
                                <a href={created.url} target="_blank" rel="noreferrer">
                                    {created.url}
                                </a>
                            </div>
                            <Button
                                variant={copied === "created" ? "success" : "primary"}
                                className="w-100"
                                onClick={() => handleCopy(created.url, "created")}
                            >
                                {copied === "created" ? "Скопійовано ✓" : "Копіювати посилання"}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {history.length > 0 && (
                <>
                    <h2 className="h6 mt-4 mb-2">Останні посилання</h2>
                    <Table size="sm" hover responsive className="payment-links-history">
                        <thead>
                            <tr>
                                <th>Дата</th>
                                <th>ФОП</th>
                                <th className="text-end">Сума</th>
                                <th>Менеджер</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {history.map((l) => (
                                <tr key={l.id}>
                                    <td className="text-nowrap">{formatDate(l.createdAt)}</td>
                                    <td>{l.legalName}</td>
                                    <td className="text-end text-nowrap">{l.amountText}</td>
                                    <td>{l.createdBy || "—"}</td>
                                    <td className="text-end text-nowrap">
                                        <Button
                                            size="sm"
                                            variant={copied === l.id ? "success" : "outline-secondary"}
                                            onClick={() => handleCopy(l.url, l.id)}
                                        >
                                            {copied === l.id ? "✓" : "Копіювати"}
                                        </Button>{" "}
                                        <Button size="sm" variant="outline-secondary" href={l.url} target="_blank">
                                            ↗
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            )}
        </div>
    );
}
